/**
 * useProjectFramework — returns workstreams/skills/roles either from
 * the project's frozen snapshot (when locked) or live framework data (when draft).
 *
 * This is the single source of framework data for ProjectDetail rendering.
 * Locked projects render entirely from their snapshot — they are completely
 * decoupled from live framework changes.
 */

import { useMemo } from 'react';
import { useWorkstreams, type VcfWorkstream, type VcfActivity, type VcfCapability, type ActivityDependency } from '@/hooks/useWorkstreams';
import { useSkills, type ResourceSkill } from '@/hooks/useSkills';
import { useResourceRoles, type ResourceRole } from '@/hooks/useAdminRoles';
import type { Project } from '@/hooks/useProjects';

export interface ProjectFramework {
  workstreams: VcfWorkstream[];
  skills: ResourceSkill[];
  roles: ResourceRole[];
  source: 'live' | 'snapshot';
  snapshotAt: string | null;
  snapshotBy: string | null;
  frameworkVersionNumber: number | null;
  isLoading: boolean;
}

interface SnapshotData {
  snapshotAt: string;
  snapshotBy: string;
  frameworkVersionId: string | null;
  frameworkVersionNumber: number | null;
  workstreams: Array<Record<string, unknown>>;
  capabilities: Array<Record<string, unknown>>;
  activities: Array<Record<string, unknown>>;
  dependencies: Array<Record<string, unknown>>;
  guardrails: Array<Record<string, unknown>>;
  generalMultipliers: Array<Record<string, unknown>>;
  skills: Array<Record<string, unknown>>;
  roles: Array<Record<string, unknown>>;
}

/**
 * Convert snapshot rows (DB format with snake_case) to the frontend types.
 */
function parseSnapshot(snapshotJson: string): {
  workstreams: VcfWorkstream[];
  skills: ResourceSkill[];
  roles: ResourceRole[];
  snapshotAt: string;
  snapshotBy: string;
  frameworkVersionNumber: number | null;
} {
  const data = JSON.parse(snapshotJson) as SnapshotData;

  // Map activities by capability_id
  const activitiesByCap = new Map<string, VcfActivity[]>();
  // Build dependency map: activityId → dependencies array
  const depsByActId = new Map<string, ActivityDependency[]>();
  for (const dep of data.dependencies) {
    const actId = dep.activity_id as string;
    if (!depsByActId.has(actId)) depsByActId.set(actId, []);
    depsByActId.get(actId)!.push({
      id: dep.id as string,
      activityId: actId,
      predecessorId: dep.predecessor_id as string,
      dependencyType: dep.dependency_type as 'FS' | 'SS' | 'FF',
      lagDays: dep.lag_days as number,
    });
  }

  for (const act of data.activities) {
    const capId = act.capability_id as string;
    const activity: VcfActivity = {
      id: act.id as string,
      capabilityId: capId,
      name: act.name as string,
      description: (act.description as string | null) ?? null,
      role: act.role as string,
      skill: (act.skill as string | null) ?? null,
      skillId: (act.skill_id as string | null) ?? null,
      baseDays: act.base_days as number,
      activityPhase: (act.activity_phase as VcfActivity['activityPhase']) ?? null,
      isMilestone: Boolean(act.is_milestone),
      sortOrder: (act.sort_order as number) ?? 0,
      isActive: Boolean(act.is_active ?? 1),
      customerDays: (act.customer_days as number) ?? 0,
      customerRole: (act.customer_role as string | null) ?? null,
      dependencies: depsByActId.get(act.id as string) || [],
    };
    if (!activitiesByCap.has(capId)) activitiesByCap.set(capId, []);
    activitiesByCap.get(capId)!.push(activity);
  }

  // Map capabilities by workstream_id
  const capsByWs = new Map<string, VcfCapability[]>();
  for (const cap of data.capabilities) {
    const wsId = cap.workstream_id as string;
    const capability: VcfCapability = {
      id: cap.id as string,
      workstreamId: wsId,
      code: cap.code as string,
      name: cap.name as string,
      description: (cap.description as string | null) ?? null,
      cls: (cap.cls as VcfCapability['cls']) ?? null,
      defaultIncluded: Boolean(cap.default_included),
      sortOrder: (cap.sort_order as number) ?? 0,
      isActive: Boolean(cap.is_active ?? 1),
      scopeText: (cap.scope_text as string | null) ?? null,
      deliverables: (cap.deliverables as string[] | null) ?? null,
      prerequisites: (cap.prerequisites as string[] | null) ?? null,
      activities: activitiesByCap.get(cap.id as string) || [],
      guardrails: [],
    };
    if (!capsByWs.has(wsId)) capsByWs.set(wsId, []);
    capsByWs.get(wsId)!.push(capability);
  }

  // Attach guardrails to capabilities
  for (const gr of data.guardrails) {
    const capId = gr.capability_id as string;
    for (const caps of capsByWs.values()) {
      const cap = caps.find(c => c.id === capId);
      if (cap) {
        cap.guardrails = cap.guardrails || [];
        cap.guardrails.push({
          id: gr.id as string,
          capabilityId: capId,
          name: gr.name as string,
          code: gr.code as string,
          baseline: (gr.baseline as string | null) ?? null,
          multiplier: (gr.multiplier as string | null) ?? null,
          baselineValue: (gr.baseline_value as number | null) ?? null,
          multiplierFactor: (gr.multiplier_factor as number | null) ?? null,
          multiplierStep: (gr.multiplier_step as number | null) ?? null,
          multiplierLabel: (gr.multiplier_label as string | null) ?? null,
          optionFactors: gr.option_factors ? JSON.parse(gr.option_factors as string) : null,
          inputType: gr.input_type as 'number' | 'select' | 'boolean',
          options: gr.options ? JSON.parse(gr.options as string) : null,
          defaultValue: (gr.default_value as string | null) ?? null,
          minValue: (gr.min_value as number | null) ?? null,
          maxValue: (gr.max_value as number | null) ?? null,
          sortOrder: (gr.sort_order as number) ?? 0,
        });
      }
    }
  }

  // Map workstreams
  const workstreams: VcfWorkstream[] = data.workstreams.map((ws) => ({
    id: ws.id as string,
    code: ws.code as string,
    name: ws.name as string,
    description: (ws.description as string | null) ?? null,
    product: (ws.product as string | null) ?? null,
    phase: (ws.phase as VcfWorkstream['phase']) ?? null,
    pillar: (ws.pillar as VcfWorkstream['pillar']) ?? null,
    category: (ws.category as string | null) ?? null,
    sortOrder: (ws.sort_order as number) ?? 0,
    isActive: Boolean(ws.is_active ?? 1),
    createdAt: (ws.created_at as string) ?? '',
    updatedAt: (ws.updated_at as string) ?? '',
    capabilities: capsByWs.get(ws.id as string) || [],
  }));

  const skills: ResourceSkill[] = data.skills.map((s) => ({
    id: s.id as string,
    code: s.code as string,
    name: s.name as string,
    description: (s.description as string | null) ?? null,
    colorBg: s.color_bg as string,
    colorText: s.color_text as string,
    sortOrder: (s.sort_order as number) ?? 0,
    isActive: Boolean(s.is_active ?? 1),
    createdAt: (s.created_at as string) ?? '',
    updatedAt: (s.updated_at as string) ?? '',
  }));

  const roles: ResourceRole[] = data.roles.map((r) => ({
    id: r.id as string,
    code: r.code as string,
    name: r.name as string,
    description: (r.description as string | null) ?? null,
    colorBg: (r.color_bg as string | null) ?? null,
    colorText: (r.color_text as string | null) ?? null,
    sortOrder: (r.sort_order as number) ?? 0,
    isActive: Boolean(r.is_active ?? 1),
    createdAt: (r.created_at as string) ?? '',
    updatedAt: (r.updated_at as string) ?? '',
  }));

  return {
    workstreams,
    skills,
    roles,
    snapshotAt: data.snapshotAt,
    snapshotBy: data.snapshotBy,
    frameworkVersionNumber: data.frameworkVersionNumber,
  };
}

export function useProjectFramework(project: Project | null | undefined): ProjectFramework {
  // Always call live hooks (React rules)
  const { data: liveWorkstreams, isLoading: wsLoading } = useWorkstreams({
    includeCapabilities: true,
    includeActivities: true,
  });
  const { data: liveSkillsData, isLoading: skillsLoading } = useSkills();
  const { data: liveRolesData, isLoading: rolesLoading } = useResourceRoles();

  const result = useMemo<ProjectFramework>(() => {
    if (project?.frameworkSnapshot) {
      // Render from snapshot
      try {
        const parsed = parseSnapshot(project.frameworkSnapshot);
        return {
          workstreams: parsed.workstreams,
          skills: parsed.skills,
          roles: parsed.roles,
          source: 'snapshot',
          snapshotAt: parsed.snapshotAt,
          snapshotBy: parsed.snapshotBy,
          frameworkVersionNumber: parsed.frameworkVersionNumber,
          isLoading: false,
        };
      } catch (e) {
        console.error('Failed to parse framework snapshot:', e);
      }
    }

    // Live mode
    return {
      workstreams: liveWorkstreams?.workstreams || [],
      skills: liveSkillsData?.skills || [],
      roles: liveRolesData?.roles || [],
      source: 'live',
      snapshotAt: null,
      snapshotBy: null,
      frameworkVersionNumber: null,
      isLoading: wsLoading || skillsLoading || rolesLoading,
    };
  }, [project?.frameworkSnapshot, liveWorkstreams, liveSkillsData, liveRolesData, wsLoading, skillsLoading, rolesLoading]);

  return result;
}
